import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Fires a single GA4 page_view on every route change - including the
 * initial mount - with the URL sanitised so we never leak PII to Google
 * Analytics.
 *
 * The gtag script is loaded from /public/index.html with
 *   gtag('config', 'G-ETQDK8MSTT', { send_page_view: false })
 * so this hook is the ONLY source of page_view events for the site.
 *
 * PII sanitisation rules:
 *   - Track application refs (e.g. WT-ABC123) in the path are replaced with
 *     a placeholder token so `/track/WT-ABC123` reports as `/track/:ref`.
 *   - Query parameters known to potentially carry customer data are
 *     stripped before we forward the URL to gtag.
 *   - Applicant form fields never enter the URL, so the Enhanced
 *     Measurement form_start / form_submit events (which report element
 *     identifiers, not values) remain safe by default.
 */
const PII_QUERY_PARAMS = new Set([
  'email', 'e-mail', 'phone', 'mobile', 'passport', 'name', 'fullname',
  'firstname', 'lastname', 'dob', 'ref', 'reference', 'token', 'ordertoken',
  'orderid', 'order_id', 'session', 'session_id', 'utm_id',
]);

// A conservative pattern for our application reference numbers.
const REF_IN_PATH_RE = /\/(WT|SGV)-[A-Z0-9]{4,}/gi;

function sanitisePath(pathname) {
  return pathname.replace(REF_IN_PATH_RE, '/:ref');
}

function sanitiseSearch(search) {
  if (!search) return '';
  const params = new URLSearchParams(search);
  let removed = false;
  for (const key of Array.from(params.keys())) {
    if (PII_QUERY_PARAMS.has(key.toLowerCase())) {
      params.delete(key);
      removed = true;
    }
  }
  if (!removed && !params.toString()) return '';
  const s = params.toString();
  return s ? `?${s}` : '';
}

export default function useGaPageview() {
  const { pathname, search } = useLocation();

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;
    const safePath = sanitisePath(pathname) + sanitiseSearch(search);
    const pageLocation = `${window.location.origin}${safePath}`;
    window.gtag('event', 'page_view', {
      page_location: pageLocation,
      page_path: safePath,
      page_title: document.title,
    });
  }, [pathname, search]);
}
